import {
  Add as AddIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Button, useNotify, useTranslate } from 'react-admin';
import { CommandParameterDraft } from '../../types/commands';
import { CommandParameterDialog } from './CommandParameterDialog';
import { normalizeDraftOrder } from './draftParameters';

interface DraftParametersPanelProps {
  parameters: CommandParameterDraft[];
  onChange: (parameters: CommandParameterDraft[]) => void;
}

let draftIdCounter = 0;

const nextDraftId = (): string => {
  draftIdCounter += 1;
  return `draft-${draftIdCounter}`;
};

/**
 * Parameters editor for the command create page.
 *
 * `POST /v3/api/commands` has no nested-create support, so parameters are kept in
 * local state here and written to `commands/{id}/parameters` once the command
 * exists. The dialog is the same one the edit page uses, in draft mode.
 */
export const DraftParametersPanel = ({ parameters, onChange }: DraftParametersPanelProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editedDraftId, setEditedDraftId] = useState<string | null>(null);

  const editedParameter = parameters.find((parameter) => parameter.draft_id === editedDraftId) || null;

  const handleAdd = () => {
    setDialogMode('create');
    setEditedDraftId(null);
    setDialogOpen(true);
  };

  const handleEdit = (parameter: CommandParameterDraft) => {
    setDialogMode('edit');
    setEditedDraftId(parameter.draft_id);
    setDialogOpen(true);
  };

  const handleRemove = (parameter: CommandParameterDraft) => {
    onChange(normalizeDraftOrder(parameters.filter((current) => current.draft_id !== parameter.draft_id)));
  };

  const handleMove = (parameter: CommandParameterDraft, offset: number) => {
    const ordered = normalizeDraftOrder(parameters);
    const index = ordered.findIndex((current) => current.draft_id === parameter.draft_id);
    const target = index + offset;

    if (index === -1 || target < 0 || target >= ordered.length) return;

    const reordered = [...ordered];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

    onChange(reordered.map((current, position) => ({ ...current, order: position + 1 })));
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditedDraftId(null);
  };

  const handleDialogSubmit = (values: Record<string, any>) => {
    const isDuplicate = parameters.some(
      (parameter) => parameter.name === values.name && parameter.draft_id !== editedDraftId,
    );

    if (isDuplicate) {
      notify('resources.commands.parameters.duplicate_name', {
        type: 'warning',
        messageArgs: { name: values.name },
      });
      return;
    }

    const { command_id: _commandId, ...parameterValues } = values;

    if (dialogMode === 'edit' && editedDraftId) {
      onChange(
        normalizeDraftOrder(
          parameters.map((parameter) =>
            parameter.draft_id === editedDraftId
              ? ({ ...parameterValues, draft_id: editedDraftId } as CommandParameterDraft)
              : parameter,
          ),
        ),
      );
      return;
    }

    onChange(
      normalizeDraftOrder([...parameters, { ...parameterValues, draft_id: nextDraftId() } as CommandParameterDraft]),
    );
  };

  const ordered = normalizeDraftOrder(parameters);

  return (
    <>
      <Card variant="outlined" sx={{ mt: 2, p: 2, width: '100%' }}>
        <CardHeader
          sx={{ p: 0, mb: 2 }}
          title={<Typography variant="h6">{translate('resources.commands.parameters.title')}</Typography>}
        />
        <CardContent sx={{ p: 0 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {translate('resources.commands.parameters.draft_hint')}
          </Alert>
          <Box sx={{ mb: 2 }}>
            <Button onClick={handleAdd} variant="contained" label="resources.commands.parameters.add">
              <AddIcon />
            </Button>
          </Box>
          {ordered.length === 0 ? (
            <Typography>{translate('resources.commands.parameters.empty')}</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate('resources.commands.parameters.fields.name')}</TableCell>
                  <TableCell>{translate('resources.commands.parameters.fields.display_name')}</TableCell>
                  <TableCell>{translate('resources.commands.parameters.fields.data_type')}</TableCell>
                  <TableCell>{translate('resources.commands.parameters.fields.ui_component')}</TableCell>
                  <TableCell>{translate('resources.commands.parameters.fields.required')}</TableCell>
                  <TableCell>{translate('resources.commands.parameters.fields.order')}</TableCell>
                  <TableCell>{translate('resources.commands.parameters.fields.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ordered.map((parameter, index) => (
                  <TableRow key={parameter.draft_id}>
                    <TableCell>{parameter.name}</TableCell>
                    <TableCell>{parameter.display_name}</TableCell>
                    <TableCell>{parameter.data_type}</TableCell>
                    <TableCell>{parameter.ui_component}</TableCell>
                    <TableCell>{translate(parameter.is_required ? 'ra.boolean.true' : 'ra.boolean.false')}</TableCell>
                    <TableCell>{parameter.order}</TableCell>
                    <TableCell>
                      <Tooltip title={translate('resources.commands.parameters.move_up')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={index === 0}
                            aria-label={translate('resources.commands.parameters.move_up')}
                            onClick={() => handleMove(parameter, -1)}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={translate('resources.commands.parameters.move_down')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={index === ordered.length - 1}
                            aria-label={translate('resources.commands.parameters.move_down')}
                            onClick={() => handleMove(parameter, 1)}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <IconButton
                        size="small"
                        aria-label={translate('ra.action.edit')}
                        onClick={() => handleEdit(parameter)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={translate('ra.action.remove')}
                        onClick={() => handleRemove(parameter)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CommandParameterDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        parameter={editedParameter}
        mode={dialogMode}
        defaultOrder={ordered.length + 1}
        onSubmit={handleDialogSubmit}
      />
    </>
  );
};
